// src/components/workout/PersonalRecords.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Trophy, Calendar } from "lucide-react";
import { format, parseISO } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";

const PersonalRecords = ({ userId, unitSystem = 'imperial' }) => {
  const [personalRecords, setPersonalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch personal records
  useEffect(() => {
    const fetchPersonalRecords = async () => {
      if (!userId) return;
    
      try {
        // First, get all workouts for this user
        const { data: workouts, error: workoutsError } = await supabase
          .from('workouts')
          .select('id')
          .eq('user_id', userId);
        
        if (workoutsError) throw workoutsError;
        
        if (!workouts || workouts.length === 0) {
          setPersonalRecords([]);
          return;
        }
        
        // Get workout IDs
        const workoutIds = workouts.map(w => w.id);
        
        // Now fetch exercises for these workouts
        const { data: exercises, error: exerciseError } = await supabase
          .from('workout_exercises')
          .select('exercise_name')
          .in('workout_id', workoutIds)
          .order('exercise_name');
    
        if (exerciseError) throw exerciseError;
    
        // Get unique exercise names
        const uniqueExercises = [...new Set(exercises.map(item => item.exercise_name))];
        
        // For each exercise, find the PR
        const records = [];
        
        for (const exercise of uniqueExercises) {
          // Get the maximum weight for this exercise
          const { data: maxWeightData, error: maxWeightError } = await supabase
            .from('workout_exercises')
            .select(`
              id,
              exercise_name,
              weight,
              workouts!inner (
                completed_at
              )
            `)
            .eq('exercise_name', exercise)
            .in('workout_id', workoutIds)
            .order('weight', { ascending: false })
            .limit(1)
            .single();
          
          if (maxWeightError && maxWeightError.code !== 'PGRST116') {
            console.error(`Error fetching max weight for ${exercise}:`, maxWeightError);
            continue;
          }
          
          if (maxWeightData) {
            records.push({
              exercise: exercise,
              weight: maxWeightData.weight,
              date: maxWeightData.workouts.completed_at
            });
          }
        }
        
        // Sort by weight (heaviest first)
        records.sort((a, b) => b.weight - a.weight);
        
        setPersonalRecords(records);
      } catch (error) {
        console.error('Error fetching personal records:', error);
        toast({
          title: 'Error',
          description: 'Failed to load personal records',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalRecords();
  }, [userId, toast]);

  if (loading && personalRecords.length === 0) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-40">
          <p className="text-muted-foreground">Loading personal records...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          <div>
            <CardTitle>Personal Records</CardTitle>
            <CardDescription>Your all-time best lifts</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {personalRecords.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {personalRecords.map((record) => (
              <div key={record.exercise} className="flex justify-between items-center p-3 border rounded-lg">
                <div>
                  <h3 className="font-medium">{record.exercise}</h3>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{format(parseISO(record.date), 'MMM d, yyyy')}</span>
                  </div>
                </div>
                <Badge variant="outline" className="px-3 py-1 text-lg">
                  {record.weight} {unitSystem === 'imperial' ? 'lbs' : 'kg'}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Trophy className="h-10 w-10 text-muted-foreground opacity-40 mx-auto mb-2" />
            <p className="text-muted-foreground">No personal records yet. Complete workouts to see your bests!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PersonalRecords;